import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Chart } from 'angular-highcharts';
import { ReplaySubject } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { HouseholdComparisonDiagramData } from '../../shared/interfaces/household-comparison.interfaces';
import { HouseholdComparisonTimeframe } from '../../shared/enums/household-comparison-timeframe.enum';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-household-comparison-chart',
    templateUrl: './household-comparison-chart.component.html',
    styleUrls: ['./household-comparison-chart.component.scss'],
})
export class HouseholdComparisonChartComponent implements OnInit, OnChanges {
    @Input() diagramData: HouseholdComparisonDiagramData;
    @Input() isDetailChart = false;

    private chartLoaded$ = new ReplaySubject();

    private readonly regularSeriesColor = '#64b42d';
    private readonly highlightedSeriesColor = '#00875a';

    chart: Chart = null;

    constructor(private translate: TranslateService) {}

    ngOnInit(): void {
        this.initializeChart();
    }

    ngOnChanges() {
        if (this.diagramData.data.length === 0) {
            return;
        }

        this.chartLoaded$.pipe(mergeMap(() => this.chart.ref$)).subscribe((chart) => {
            // remove all existing series
            while (chart.series.length > 0) {
                chart.series[0].remove(false);
            }

            const chartTitle = this.isDetailChart
                ? null
                : this.determineTitleBasedOnTimeframe(this.diagramData.timeframe);
            // update the chart
            chart.update(
                {
                    xAxis: {
                        tickPositions: [
                            this.diagramData.data.first().x,
                            this.diagramData.data.last().x,
                        ],
                        plotLines: [
                            {
                                color: this.regularSeriesColor,
                                width: 1.5,
                                value: this.diagramData.averageBucketIdx + 0.5,
                                dashStyle: 'Dash',
                                label: {
                                    text: `⌀ ${this.diagramData.averageConsumption} kWh`,
                                    verticalAlign: 'bottom',
                                    style: {
                                        fontFamily: 'Innogy Regular, sans-serif',
                                        fontSize: this.isDetailChart ? '16px' : '10px',
                                        color: this.regularSeriesColor,
                                    },
                                    align: 'center',
                                    rotation: 0,
                                    y: this.isDetailChart ? 27 : 20,
                                },
                            },
                        ],
                    },
                    title: {
                        text: chartTitle,
                    },
                    series: [
                        {
                            type: 'column',
                            name: 'test',
                            data: this.diagramData.data.map((data) => ({
                                y: data.y,
                                x: data.x,
                                color: data.highlighted
                                    ? this.highlightedSeriesColor
                                    : this.regularSeriesColor,
                                custom: {
                                    highlighted: data.highlighted,
                                    calloutValue: this.diagramData.highlightedBucketCalloutValue,
                                },
                            })),
                        },
                    ],
                },
                true,
                true
            );
        });
    }

    /**
     * Determines the title based on the timeframe.
     * @param timeframe
     * @private
     */
    private determineTitleBasedOnTimeframe(timeframe: HouseholdComparisonTimeframe): string {
        const currentLang = this.translate.currentLang;

        switch (timeframe) {
            case HouseholdComparisonTimeframe.CURRENT_YEAR:
                return this.translate.instant(
                    'screens.dashboard.householdComparison.detailLabels.currentYear'
                );
            case HouseholdComparisonTimeframe.LAST_YEAR:
                return this.translate.instant(
                    'screens.dashboard.householdComparison.detailLabels.previousYear'
                );
            case HouseholdComparisonTimeframe.LAST_MONTH:
                return moment().locale(currentLang).format('MMMM YYYY');
        }
    }

    /**
     * Initializes the chart.
     * @private
     */
    private initializeChart(): void {
        const self = this;
        const myConsumptionString = this.translate.instant(
            'screens.dashboard.householdComparison.detailLabels.myConsumption'
        );
        const low = this.translate.instant(
            'screens.dashboard.householdComparison.detailLabels.low'
        );
        const high = this.translate.instant(
            'screens.dashboard.householdComparison.detailLabels.high'
        );

        this.chart = new Chart({
            chart: {
                type: 'column',
                backgroundColor: 'transparent',
                events: {
                    load() {
                        self.chartLoaded$.next(null);
                    },
                },
                marginLeft: self.isDetailChart ? 60 : 50,
                marginTop: 20,
            },
            title: {
                text: null,
                style: {
                    fontFamily: 'Innogy medium, sans-serif',
                    fontSize: self.isDetailChart ? '16px' : '10px',
                    color: '#00875a',
                },
                y: 28,
                align: 'right',
            },
            xAxis: {
                labels: {
                    style: {
                        fontFamily: 'Innogy Regular, sans-serif',
                        fontSize: self.isDetailChart ? '16px' : '10px',
                        color: '#333333',
                    },
                    formatter(evt) {
                        if (evt.isFirst) {
                            return low;
                        }
                        if (evt.isLast) {
                            return high;
                        }
                        return '';
                    },
                    rotation: 0,
                    autoRotation: [0],
                },
            },
            yAxis: {
                endOnTick: false,
                title: {
                    text: 'kWh',
                    align: 'high',
                    textAlign: 'right',
                    x: 35,
                    y: -5,
                    rotation: 0,
                    style: {
                        fontFamily: 'Innogy Regular, sans-serif',
                        fontSize: '12px',
                        color: '#333333',
                    },
                },
                labels: {
                    align: 'right',
                    x: -8,
                    y: 5,
                    style: {
                        fontFamily: 'Innogy Regular, sans-serif',
                        fontSize: '12px',
                        color: '#333333',
                    },
                    formatter(evt) {
                        if (evt.isFirst) {
                            return '';
                        }
                        return `${evt.value}`;
                    },
                },
            },
            tooltip: {
                useHTML: true,
                formatter() {
                    const style = `style="border-color: ${this.color}"`;
                    const unit = 'kWh';

                    let valueFormatted = this.y.toFixed(0);

                    const p = this.point as any;
                    const highlighted = p.custom.highlighted;
                    if (highlighted) {
                        valueFormatted = p.custom.calloutValue.toFixed(0);
                    }
                    const value = `<div class="body"> ${valueFormatted} ${unit}</div>`;
                    const calloutTitleStr = `${highlighted ? myConsumptionString : ''}`;

                    return `<div class="column-callout" ${style}>${calloutTitleStr} ${value}</div>`;
                },
                borderColor: 'white',
                borderWidth: 0,
                borderRadius: 0,
                backgroundColor: 'transparent',
                shadow: false,
            },
            legend: {
                enabled: false,
            },
            plotOptions: {
                column: {
                    borderRadius: 3,
                    groupPadding: 0,
                    pointPadding: 0.1,
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
            },
            credits: {
                enabled: false,
            },
        });
    }
}
